import type { CardDef, CardVariant, CardEffect, DiceNotation, DomainId, Stat, Stats } from "./types";
import { STATUS_DEFS } from "./types";
import { statMod } from "./heroComputed";

const STAT_RU: Record<Stat, string> = {
  strength: "СИЛ", dexterity: "ЛОВ", intelligence: "ИНТ", wisdom: "МУД", constitution: "ТЕЛ",
};

export function describeApplyStatus(e: Extract<CardEffect, { type: "applyStatus" }>): string {
  const def = STATUS_DEFS[e.status];
  const tgt = e.target === "self" ? "себе" : "цели";
  let detail = "";
  switch (e.status) {
    case "poison":
    case "bleed": detail = ` 1d${e.dieSides ?? 4}${e.ttl ? ` ×${e.ttl}х` : ""}`; break;
    case "damageDie": detail = ` +${e.stacks}d${e.dieSides ?? 4}`; break;
    case "hitBoost": detail = ` +${e.stacks}`; break;
    case "statDebuff": detail = ` ${(e.delta ?? 0) > 0 ? "+" : ""}${e.delta} ${e.stat ? STAT_RU[e.stat] : ""}`; break;
    case "stun": detail = ` ${e.stacks}`; break;
    case "weak":
    case "levelDown":
    case "regen":
    case "blessing":
    case "chill": detail = ` ${e.stacks}${e.ttl ? ` ×${e.ttl}х` : ""}`; break;
    case "numbness": detail = ` ${e.stacks}`; break;
  }
  return `${def.label}${detail} (${tgt})`;
}

function formulaText(base: number, dice?: DiceNotation, statScale?: { stat: Stat; mult: number }, levelScale?: { mult: number }): string {
  const parts: string[] = [];
  if (base) parts.push(String(base));
  if (dice) parts.push(`${dice.count}d${dice.sides}`);
  if (statScale) parts.push(`${statScale.mult}×${STAT_RU[statScale.stat]}`);
  if (levelScale) parts.push(`${levelScale.mult}×Ур`);
  return parts.length > 0 ? parts.join("+") : "0";
}

function describeEffect(e: CardEffect): string {
  switch (e.type) {
    case "damage": return `Урон +${formulaText(e.base, e.dice, e.statScale, e.levelScale)}`;
    case "heal": return `Лечение +${formulaText(e.base, e.dice, e.statScale, e.levelScale)}`;
    case "draw": return `+${e.count} карт`;
    case "bonusAttack": return `${e.scope === "nextAttack" ? "След. атака" : "Все атаки"}: +${STAT_RU[e.bonusFromStat]}`;
    case "armorDebuff": return e.value < 0 ? `+${-e.value} армора` : `−${e.value} армора цели`;
    case "bonusDraw": return `При крите: +${e.count} карт`;
    case "armorBuff": return `+${formulaText(e.base, e.dice, e.statScale)} армора`;
    case "selfDamage": return `Себе: −${formulaText(e.base, e.dice, e.statScale)} HP`;
    case "applyStatus": return describeApplyStatus(e);
  }
}

export function describeEnhancement(card: CardDef, domainId: DomainId, side: "hope" | "fear"): string {
  const effects = card.enhancements[domainId]?.[side] ?? [];
  if (effects.length === 0) return "";
  return effects.map(describeEffect).join(" · ");
}

export interface Range { min: number; max: number }

function rangeForStatScale(scale: { stat: Stat; mult: number } | undefined, stats: Stats): number {
  if (!scale) return 0;
  return statMod(stats[scale.stat] ?? 0) * scale.mult;
}

function rangeForEffect(effect: Extract<CardEffect, { type: "damage" | "heal" }>, stats: Stats): Range {
  let min = effect.base;
  let max = effect.base;
  if (effect.dice) {
    min += effect.dice.count;
    max += effect.dice.count * effect.dice.sides;
  }
  const flat = rangeForStatScale(effect.statScale, stats);
  return { min: min + flat, max: max + flat };
}

export function computeDamageRange(variant: CardVariant, stats: Stats): Range | null {
  const damage = variant.effects.find((e) => e.type === "damage") as Extract<CardEffect, { type: "damage" }> | undefined;
  if (!damage) return null;
  return rangeForEffect(damage, stats);
}

export function computeHealRange(variant: CardVariant, stats: Stats): Range | null {
  const heal = variant.effects.find((e) => e.type === "heal") as Extract<CardEffect, { type: "heal" }> | undefined;
  if (!heal) return null;
  return rangeForEffect(heal, stats);
}

export function formatRange(r: Range): string {
  return r.min === r.max ? `${r.min}` : `${r.min}–${r.max}`;
}
